/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ConfigProvider, Progress } from 'antd';
import Swal, { type SweetAlertIcon, type SweetAlertResult } from 'sweetalert2';
import theme from '@/themeConfig';
import HandleErrorApi, { type HandleErrorApiProps } from '@/components/common/HandleErrorApi';

const enableAnimation = false;

export type ShowHandleErrorApiOptions = HandleErrorApiProps & {
    /** Tự đóng sau (ms). Mặc định 10000 như errorAsync. */
    timer?: number;
    /** true = không tự đóng, chờ user thao tác. */
    manualClose?: boolean;
};

export class SweetAlertService {


    private static async fireWithCountdown(
        icon: SweetAlertIcon,
        title: string,
        html: string | undefined,
        text: string | undefined,
        confirmButtonColor: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): Promise<SweetAlertResult> {
        let timerInterval: any;
        const duration = 10000; // 10 giây
        const totalSec = duration / 1000;

        const result = await Swal.fire({
            icon,
            title: title,
            html: html,
            // text: title,
            ...(manualClose
                ? {}
                : {
                    timer: duration,
                    timerProgressBar: true,
                }),
            confirmButtonText: manualClose ? 'OK' : `OK (${totalSec})`,
            confirmButtonColor,
            animation: enableAnimation,
            didOpen: () => {
                if (manualClose) return;
                const confirmBtn = Swal.getConfirmButton();
                if (confirmBtn) {
                    let remaining = totalSec;
                    timerInterval = setInterval(() => {
                        remaining--;
                        if (remaining >= 0) {
                            confirmBtn.textContent = `OK (${remaining})`;
                        }
                    }, 1000);
                }
            },
            willClose: () => {
                clearInterval(timerInterval);
            }
        });

        if ((requireAction || result.isConfirmed) && callback) {
            await callback();
        }
        return result;
    }

    private static fireWithCountdownSync(
        icon: SweetAlertIcon,
        title: string,
        text: string | undefined,
        confirmButtonColor: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): void {
        let timerInterval: any;
        const duration = 10000; // 10 giây
        const totalSec = duration / 1000;

        const content = text ?? title;
        const useHtml = !!content && content.includes('<');

        Swal.fire({
            icon,
            ...(useHtml
                ? { html: content }
                : {
                    title: text,
                    text: title,
                }),
            ...(manualClose
                ? {}
                : {
                    timer: duration,
                    timerProgressBar: true,
                }),
            confirmButtonText: manualClose ? 'OK' : `OK (${totalSec})`,
            confirmButtonColor,
            animation: enableAnimation,
            didOpen: () => {
                if (manualClose) return;
                const confirmBtn = Swal.getConfirmButton();
                if (confirmBtn) {
                    let remaining = totalSec;
                    timerInterval = setInterval(() => {
                        remaining--;
                        if (remaining >= 0) {
                            confirmBtn.textContent = `OK (${remaining})`;
                        }
                    }, 1000);
                }
            },
            willClose: () => {
                clearInterval(timerInterval);
            }
        }).then((result) => {
            if ((requireAction || result.isConfirmed) && callback) {
                callback();
            }
        });
    }

    // ========== BLOCKING VERSIONS (ASYNC) ==========
    /**
     * Hiển thị thông báo thành công (chờ user tương tác)
     */
    static async success(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): Promise<SweetAlertResult> {
        return this.fireWithCountdown('success', title ,"", text, '#28a745', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo lỗi (chờ user tương tác)
     */
    static async error(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): Promise<SweetAlertResult> {
        return this.fireWithCountdown('error', title, "", text, '#dc3545', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo cảnh báo (chờ user tương tác)
     */
    static async warning(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): Promise<SweetAlertResult> {
        return this.fireWithCountdown('warning', title, text, text, '#ffc107', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo thông tin (chờ user tương tác)
     */
    static async info(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): Promise<SweetAlertResult> {
        return this.fireWithCountdown('info', title, "", text, '#17a2b8', callback, requireAction, manualClose);
    }

    // ========== NON-BLOCKING VERSIONS (SYNC) ==========
    /**
     * Hiển thị thông báo thành công (không chờ, code tiếp tục chạy)
     */
    static successAsync(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): void {
        this.fireWithCountdownSync('success', title, text, '#28a745', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo lỗi (không chờ, code tiếp tục chạy)
     */
    static errorAsync(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): void {
        this.fireWithCountdownSync('error', title, text, '#dc3545', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo cảnh báo (không chờ, code tiếp tục chạy)
     */
    static warningAsync(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): void {
        this.fireWithCountdownSync('warning', title, text, '#ffc107', callback, requireAction, manualClose);
    }

    /**
     * Hiển thị thông báo thông tin (không chờ, code tiếp tục chạy)
     */
    static infoAsync(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        requireAction: boolean = false,
        manualClose: boolean = false,
    ): void {
        this.fireWithCountdownSync('info', title, text, '#17a2b8', callback, requireAction, manualClose);
    }

    // ========== CONFIRM DIALOGS ==========
    /**
     * Dialog xác nhận với Yes/No (chờ user chọn)
     */
    static async confirm(
        title: string,
        text?: string,
        callback?: () => Promise<void> | void,
        confirmButtonText = 'Có',
        cancelButtonText = 'Không'
    ): Promise<boolean> {
        const result = await Swal.fire({
            icon: 'question',
            title: text,
            text: title,
            showCancelButton: true,
            confirmButtonText: confirmButtonText,
            cancelButtonText: cancelButtonText,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#dc3545',
            reverseButtons: true,
            animation: enableAnimation
        });

        if (result.isConfirmed && callback) {
            await callback();
        }

        return result.isConfirmed;
    }

    /**
     * Dialog xác nhận xóa (chờ user chọn)
     */
    static async confirmDelete(
        title = 'Bạn có chắc chắn muốn xóa?',
        text = 'Hành động này không thể hoàn tác!',
        callback?: () => Promise<void> | void
    ): Promise<boolean> {
        const result = await Swal.fire({
            icon: 'warning',
            title: text,
            text: title,
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            reverseButtons: true,
            animation: enableAnimation
        });

        if (result.isConfirmed && callback) {
            await callback();
        }
        return result.isConfirmed;
    }

    /**
     * Dialog xác nhận với Yes/No (không chờ, code tiếp tục chạy)
     */
    static confirmAsync(
        title: string,
        text?: string,
        callback?: (confirmed: boolean) => Promise<void> | void,
        confirmButtonText = 'Có',
        cancelButtonText = 'Không'
    ): void {
        Swal.fire({
            icon: 'question',
            title: text,
            text: title,
            showCancelButton: true,
            confirmButtonText: confirmButtonText,
            cancelButtonText: cancelButtonText,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#dc3545',
            reverseButtons: true,
            animation: enableAnimation
        }).then(async (result) => {
            if (callback) {
                await callback(result.isConfirmed);
            }
        });
    }

    // ========== TOAST NOTIFICATIONS ==========
    /**
     * Toast notification (luôn non-blocking)
     */
    static toast(
        icon: SweetAlertIcon,
        title: string,
        timer = 3000,
        callback?: () => Promise<void> | void
    ): void {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon,
            text: title,
            showConfirmButton: false,
            timer,
            timerProgressBar: true,
            animation: enableAnimation,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
            didClose: async () => {
                if (callback) {
                    await callback();
                }
            }
        });
    }

    // ========== LOADING DIALOGS ==========
    /**
     * Loading dialog
     */
    static showLoading(title = 'Đang xử lý...', text?: string, classMain?: {}): void {
        Swal.fire({
            title: title || 'Đang xử lý...',
            html: text || 'Vui lòng đợi trong giây lát',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            animation: enableAnimation,
            customClass: classMain,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    }

    /**
     * Đóng loading dialog
     */
    static hideLoading(): void {
        SweetAlertService.unmountProgress();
        SweetAlertService.unmountHandleError();
        Swal.close();
    }

    // ========== INPUT DIALOGS ==========
    /**
     * Input dialog (chờ user nhập)
     */
    static async input(
        title: string,
        inputType: 'text' | 'email' | 'password' | 'number' | 'textarea' = 'text',
        placeholder?: string,
        defaultValue?: string,
        callback?: (value: string) => Promise<void> | void
    ): Promise<string | null> {
        const result = await Swal.fire({
            text: title,
            input: inputType,
            inputPlaceholder: placeholder,
            inputValue: defaultValue,
            showCancelButton: true,
            confirmButtonText: 'OK',
            cancelButtonText: 'Hủy',
            animation: enableAnimation,
            inputValidator: (value) => {
                if (!value) {
                    return 'Vui lòng nhập giá trị!';
                }
                return null;
            }
        });

        if (result.isConfirmed && callback) {
            await callback(result.value);
        }
        return result.isConfirmed ? result.value : null;
    }

    /**
     * Select dialog (chờ user chọn)
     */
    static async select(
        title: string,
        options: Record<string, string>,
        placeholder = 'Chọn một tùy chọn',
        callback?: (value: string) => Promise<void> | void
    ): Promise<string | null> {
        const result = await Swal.fire({
            text: title,
            input: 'select',
            inputOptions: options,
            inputPlaceholder: placeholder,
            showCancelButton: true,
            confirmButtonText: 'OK',
            cancelButtonText: 'Hủy',
            animation: enableAnimation,
            inputValidator: (value) => {
                if (!value) {
                    return 'Vui lòng chọn một tùy chọn!';
                }
                return null;
            }
        });

        if (result.isConfirmed && callback) {
            await callback(result.value);
        }
        return result.isConfirmed ? result.value : null;
    }

    // ========== ADVANCED FEATURES ==========
    private static progressRoot: Root | null = null;
    private static progressPercent = 0;
    private static progressStatus: 'active' | 'success' | 'exception' = 'active';

    private static unmountProgress(): void {
        if (!SweetAlertService.progressRoot) return;
        SweetAlertService.progressRoot.unmount();
        SweetAlertService.progressRoot = null;
    }

    private static renderAntdProgress(): void {
        if (!SweetAlertService.progressRoot) return;
        const percent = Math.max(0, Math.min(100, Math.round(SweetAlertService.progressPercent)));
        SweetAlertService.progressRoot.render(
            React.createElement(
                ConfigProvider,
                { theme },
                React.createElement(Progress, {
                    percent,
                    status: SweetAlertService.progressStatus,
                    percentPosition: { align: 'center', type: 'inner' },
                    size: ['100%', 20],
                    strokeColor: percent >= 100 ? undefined : { from: '#0c68e9', to: '#69b1ff' },
                    format: (value) => `${value ?? 0}%`,
                }),
            ),
        );
    }

    private static buildProgressHtml(text: string | undefined): string {
        const safeText = text ?? 'Vui lòng đợi trong giây lát';
        return `
            <p id="swal-progress-desc" style="margin: 0 0 16px; color: #595959; font-size: 14px; line-height: 1.5;">
                ${safeText}
            </p>
            <div id="swal-antd-progress" style="width: 100%; min-width: 280px; text-align: left;"></div>
        `;
    }

    /**
     * Loading dialog với Progress Ant Design (% quá trình).
     * Dùng kèm {@link updateProgress} và {@link hideLoading}.
     */
    static showProgress(title = 'Đang xử lý...', text?: string, initialPercent = 0): void {
        SweetAlertService.unmountProgress();
        SweetAlertService.progressPercent = Math.max(0, Math.min(100, initialPercent));
        SweetAlertService.progressStatus = SweetAlertService.progressPercent >= 100 ? 'success' : 'active';

        Swal.fire({
            title: title || 'Đang xử lý...',
            html: SweetAlertService.buildProgressHtml(text),
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            animation: enableAnimation,
            didOpen: () => {
                const el = document.getElementById('swal-antd-progress');
                if (!el) return;
                SweetAlertService.progressRoot = createRoot(el);
                SweetAlertService.renderAntdProgress();
            },
            willClose: () => {
                SweetAlertService.unmountProgress();
            },
        });
    }

    /**
     * Cập nhật % / mô tả trên dialog Progress đang mở.
     */
    static updateProgress(percent: number, text?: string, status?: 'active' | 'success' | 'exception'): void {
        SweetAlertService.progressPercent = Math.max(0, Math.min(100, percent));
        SweetAlertService.progressStatus =
            status ?? (SweetAlertService.progressPercent >= 100 ? 'success' : 'active');
        SweetAlertService.renderAntdProgress();
        if (text != null) {
            const desc = document.getElementById('swal-progress-desc');
            if (desc) desc.textContent = text;
        }
    }

    /**
     * Loading với progress bar tùy chỉnh (tự chạy callback theo %).
     */
    static async showLoadingWithProgress(
        title = 'Đang xử lý...',
        progressCallback?: (progress: number) => Promise<void> | void
    ): Promise<void> {
        SweetAlertService.showProgress(title, undefined, 0);
        if (progressCallback) {
            for (let i = 0; i <= 100; i += 10) {
                SweetAlertService.updateProgress(i);
                await progressCallback(i);
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }
    }

    /**
     * Multi-step confirmation
     */
    static async multiStepConfirm(
        steps: Array<{
            title: string;
            text?: string;
            icon?: SweetAlertIcon;
        }>,
        callback?: () => Promise<void> | void
    ): Promise<boolean> {
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            const isLastStep = i === steps.length - 1;

            const result = await Swal.fire({
                title: step.title,
                text: step.text,
                icon: step.icon || 'question',
                showCancelButton: true,
                confirmButtonText: isLastStep ? 'Xác nhận' : 'Tiếp tục',
                cancelButtonText: 'Hủy',
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#dc3545',
                reverseButtons: true,
                animation: enableAnimation
            });

            if (!result.isConfirmed) {
                return false;
            }
        }

        if (callback) {
            await callback();
        }
        return true;
    }

    /**
     * Error with retry option
     */
    static async errorWithRetry(
        title: string,
        text?: string,
        retryCallback?: () => Promise<void> | void
    ): Promise<'retry' | 'cancel'> {
        const result = await Swal.fire({
            icon: 'error',
            text: title,
            title: text,
            showCancelButton: true,
            confirmButtonText: 'Thử lại',
            cancelButtonText: 'Hủy',
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#dc3545',
            reverseButtons: true,
            animation: enableAnimation
        });

        if (result.isConfirmed && retryCallback) {
            await retryCallback();
            return 'retry';
        }
        return 'cancel';
    }

    private static handleErrorRoot: Root | null = null;

    private static unmountHandleError(): void {
        if (!SweetAlertService.handleErrorRoot) return;
        SweetAlertService.handleErrorRoot.unmount();
        SweetAlertService.handleErrorRoot = null;
    }

    /**
     * Dialog lỗi API: render {@link HandleErrorApi} làm content của SweetAlert.
     * Mặc định tự đóng sau 10s (progress bar) giống {@link errorAsync}, trừ khi `manualClose`.
     */
    static async showHandleErrorApi(
        options?: ShowHandleErrorApiOptions,
    ): Promise<'retry' | 'back' | 'dismiss'> {
        SweetAlertService.unmountHandleError();
        const actionRef: { current: 'retry' | 'back' | 'dismiss' } = { current: 'dismiss' };
        const { timer, manualClose, ...contentOptions } = options ?? {};
        const duration = timer ?? 10000;

        await Swal.fire({
            html: '<div id="swal-handle-error-api"></div>',
            showConfirmButton: contentOptions.variant === 'warning',
            confirmButtonText: 'Đã hiểu',
            showCancelButton: false,
            allowOutsideClick: true,
            allowEscapeKey: true,
            animation: enableAnimation,
            ...(manualClose || duration <= 0
                ? {}
                : {
                    timer: duration,
                    timerProgressBar: true,
                }),
            customClass: {
                popup: 'swal-handle-error-api',
                htmlContainer: 'swal-handle-error-api-html',
            },
            didOpen: (popup) => {
                if (!manualClose && duration > 0) {
                    popup.addEventListener('mouseenter', Swal.stopTimer);
                    popup.addEventListener('mouseleave', Swal.resumeTimer);
                }
                const el = document.getElementById('swal-handle-error-api');
                if (!el) return;
                SweetAlertService.handleErrorRoot = createRoot(el);
                SweetAlertService.handleErrorRoot.render(
                    React.createElement(
                        ConfigProvider,
                        { theme },
                        React.createElement(HandleErrorApi, {
                            ...contentOptions,
                            fullHeight: false,
                            onRetry: () => {
                                actionRef.current = 'retry';
                                Swal.close();
                            },
                            onBack: () => {
                                actionRef.current = 'back';
                                Swal.close();
                            },
                        }),
                    ),
                );
            },
            willClose: () => {
                SweetAlertService.unmountHandleError();
            },
        });

        const action = actionRef.current;
        if (action === 'retry') {
            if (options?.onRetry) {
                await options.onRetry();
            } else if (typeof window !== 'undefined') {
                window.location.reload();
            }
        } else if (action === 'back') {
            if (options?.onBack) {
                await options.onBack();
            } else if (typeof window !== 'undefined') {
                if (window.history.length > 1) {
                    window.history.back();
                } else {
                    window.location.href = '/';
                }
            }
        }

        return action;
    }

    /**
     * Như {@link showHandleErrorApi} nhưng không chờ (fire-and-forget), giống {@link errorAsync}.
     */
    static showHandleErrorApiAsync(options?: ShowHandleErrorApiOptions): void {
        void SweetAlertService.showHandleErrorApi(options);
    }
}
